/**
 * MyAccessPassPage.jsx — signed-in user's AccessPass dashboard
 * ------------------------------------------------------------
 * Mounted at `/dashboard/access-passes`. Shows every AccessPass linked
 * to the calling user (matched by user_id OR email), with the QR token
 * lazily fetched on click (token never lives in list response).
 */
import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import AccessPassCard from "../../components/AccessPassCard";
import {
  getMyPasses, getPassWithQr, resendPass, formatApiError,
  getAccessFeatureCosts, lostPassReissue,
} from "../../lib/accessPassApi";
import { Button } from "../../components/ui/button";
import {
  ScanLine, RefreshCw, ShieldCheck, Loader2, CalendarRange,
  AlertCircle, AlertTriangle, Inbox, Gem,
} from "lucide-react";
import { toast } from "sonner";

export default function MyAccessPassPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);      // { pass, qrToken }
  const [opening, setOpening] = useState(null);   // pass id being opened
  const [featureCosts, setFeatureCosts] = useState({});
  const [reissuing, setReissuing] = useState(false);
  const [reissueConfirm, setReissueConfirm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await getMyPasses();
      setRows(r.rows || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getAccessFeatureCosts().then(setFeatureCosts).catch(() => {});
  }, []);

  const openPass = async (pass) => {
    setOpening(pass.id);
    try {
      const r = await getPassWithQr(pass.id);
      setActive({ pass: r.pass, qrToken: r.qr_token });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setOpening(null);
    }
  };

  const handleResend = async () => {
    if (!active?.pass?.id) return;
    try {
      const r = await resendPass(active.pass.id);
      setActive((a) => ({ ...a, qrToken: r.qr_token }));
      toast.success("New QR issued — older copies are now invalid.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reissueCost = featureCosts.access_pass_reissue?.cost || 50;

  const handleReissue = async () => {
    if (!active?.pass?.id) return;
    setReissuing(true);
    try {
      const r = await lostPassReissue(active.pass.id, "lost_pass");
      setActive((a) => ({
        ...a,
        qrToken: r.qr_token,
        pass: { ...a.pass, ...r.pass },
      }));
      toast.success(
        `Pass re-issued · ${r.diploks_charged} Diploks charged · balance ${r.balance_after}`
      );
      setReissueConfirm(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "insufficient_balance") {
        toast.error(`Need ${detail.cost} Diploks — only ${detail.balance_before} available.`);
      } else if (detail?.code === "consent_required") {
        toast.error("Please accept Diploks Usage Consent in your wallet first.");
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setReissuing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-8 py-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
              Diplomexa AccessPass
            </p>
            <h1 className="mt-1 text-3xl font-bold text-[#0A192F]">
              My event passes
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Scan-ready entry passes for every event you&apos;re attending.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            data-testid="my-passes-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading passes…
          </div>
        ) : rows.length === 0 ? (
          <div
            className="text-center py-16 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50/60"
            data-testid="my-passes-empty"
          >
            <Inbox className="h-10 w-10 text-slate-300 mx-auto" />
            <p className="mt-3 font-semibold text-[#0A192F]">No passes yet</p>
            <p className="text-sm text-slate-500">
              Once you register for an event, your AccessPass appears here.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rows.map((p) => (
              <button
                key={p.id}
                onClick={() => openPass(p)}
                className="group text-left rounded-sm bg-white border border-slate-200 p-5 hover:border-brand-gold hover:shadow-editorial transition-all duration-300 relative overflow-hidden"
                data-testid={`my-pass-tile-${p.id}`}
              >
                {/* Hairline gold top stripe on hover */}
                <div aria-hidden className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-start justify-between">
                  <div className="h-10 w-10 rounded-sm bg-brand-navy-dark text-brand-gold flex items-center justify-center group-hover:scale-105 transition-transform">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <span
                    className={`text-[10px] uppercase font-bold tracking-[0.2em] rounded-sm px-2 py-1 border ${
                      p.status === "active"
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : p.status === "pending"
                        ? "border-amber-300 bg-amber-50 text-amber-700"
                        : "border-slate-300 bg-slate-50 text-slate-500"
                    }`}
                  >
                    {p.status}
                  </span>
                </div>
                <p className="eyebrow mt-4 text-brand-gold/80">
                  {p.role || p.pass_type}
                </p>
                <p className="mt-1.5 font-display text-xl text-brand-navy-dark line-clamp-2 leading-tight">
                  {p.event_title || "Conference"}
                </p>
                <p className="mt-1 text-xs text-slate-500">{p.full_name}</p>
                {p.valid_from && (
                  <p className="mt-4 flex items-center gap-1.5 text-[11px] text-slate-500 font-mono">
                    <CalendarRange className="h-3 w-3 text-brand-gold" />
                    {new Date(p.valid_from).toLocaleDateString()}
                  </p>
                )}
                <p className="mt-4 inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-bold text-brand-midblue group-hover:text-brand-gold transition-colors">
                  {opening === p.id ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1.5" />
                  ) : (
                    <ScanLine className="h-3 w-3 mr-1.5" />
                  )}
                  Show QR
                </p>
              </button>
            ))}
          </div>
        )}

        {active && (
          <div
            className="fixed inset-0 z-40 bg-[#0A192F]/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setActive(null)}
            data-testid="my-pass-modal"
          >
            <div
              className="max-w-xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <AccessPassCard pass={active.pass} qrToken={active.qrToken} />
              <div className="mt-4 flex items-center justify-end gap-2 flex-wrap">
                {active.pass?.status === "active" && (
                  <Button
                    onClick={() => setReissueConfirm(true)}
                    className="bg-[#D4AF37]/15 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/25"
                    data-testid="my-pass-lost-reissue"
                  >
                    <Gem className="h-4 w-4 mr-2" />
                    Lost pass? Premium · Uses Diploks
                  </Button>
                )}
                <Button
                  className="bg-[#D4AF37] hover:bg-[#B89227] text-[#0A192F]"
                  onClick={() => setActive(null)}
                  data-testid="my-pass-close"
                >
                  Close
                </Button>
              </div>
              {active.pass?.status !== "active" && (
                <div className="mt-3 flex items-center justify-center gap-2 text-sm text-amber-100">
                  <AlertCircle className="h-4 w-4" />
                  This pass is {active.pass?.status}. The QR is hidden until
                  it becomes active.
                </div>
              )}
            </div>
          </div>
        )}

        {reissueConfirm && active && (
          <div
            className="fixed inset-0 z-50 bg-[#0A192F]/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setReissueConfirm(false)}
            data-testid="reissue-confirm-modal"
          >
            <div
              className="max-w-md w-full rounded-2xl bg-white p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                  <Gem className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <div>
                  <h3 className="font-bold text-[#0A192F]">Reissue your AccessPass?</h3>
                  <p className="text-[10px] uppercase tracking-wider text-[#D4AF37] font-bold">
                    Premium · Uses Diploks
                  </p>
                </div>
              </div>
              <p className="text-sm text-slate-600">
                We&apos;ll rotate your QR token immediately. The old QR
                code on your phone, email or screenshots will stop
                working at once. A {reissueCost}-Diploks charge will
                be deducted from your wallet.
              </p>
              <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                Once the new QR is issued, your old QR cannot be reused.
                Make sure you really need a fresh code.
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setReissueConfirm(false)}
                  disabled={reissuing}
                  data-testid="reissue-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleReissue}
                  disabled={reissuing}
                  className="bg-[#0A192F] hover:bg-[#0F2C4F]"
                  data-testid="reissue-confirm"
                >
                  {reissuing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Rotating…
                    </>
                  ) : (
                    <>
                      <Gem className="h-4 w-4 mr-2" />
                      Confirm · {reissueCost} Diploks
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
